import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import { subscribeToasts, type ToastMessage } from '../../lib/toast';
import { cn } from '../../lib/utils';

const DURATION_MS = 3200;

export function NotificationHost() {
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToasts((nextToast) => {
      setToast(nextToast);

      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = window.setTimeout(() => setToast(null), DURATION_MS);
    });

    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
      unsubscribe();
    };
  }, []);

  if (!toast) return null;

  const isSuccess = toast.kind === 'success';
  const Icon = isSuccess ? CheckCircle2 : XCircle;

  // Floats above the page (fixed, no layout space) so a toast never shifts the content below it.
  return (
    <div className="pointer-events-none fixed inset-x-0 top-3 z-[100] flex justify-center px-4" role={isSuccess ? 'status' : 'alert'} aria-live={isSuccess ? 'polite' : 'assertive'}>
      <div className="flex max-w-md items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-xs font-medium text-foreground shadow-lg sm:text-sm">
        <Icon className={cn('h-4 w-4 shrink-0', isSuccess ? 'text-success' : 'text-destructive')} />
        <span>{toast.message}</span>
      </div>
    </div>
  );
}
